import { Button, Field, Input, Select, Spinner } from "@deligoo/ui"
import clsx from "clsx"
import { FormEvent, useEffect, useState } from "react"
import {
  FieldValues,
  useFieldArray,
  UseFieldArrayUpdate,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import NumberFormat from "react-number-format"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { selectOrderById } from "@/store/orders"
import { AdditionalFee, Order } from "@/types"
import { ADDITIONAL_FEES_TYPES, fetchOrder, formatPrice } from "@/utils"

import cls from "../EditOrderPanel.module.scss"

export const Costs = ({ orderId }: { orderId: Order["id"] }) => {
  const order = useAppSelector(selectOrderById(orderId))

  const { register, watch, setValue } = useFormContext()

  const { errors } = useFormState()

  const distance_cost_subunit: number = useWatch({
    name: "distance_cost_subunit",
  })

  const { fields, append, update } = useFieldArray({
    name: "additional_fees_attributes",
    keyName: "additionalFeeId",
  })

  const additionalFees = watch("additional_fees_attributes")

  const [totalPriceSubunit, setTotalPriceSubunit] = useState(0)

  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    async function getFullOrder() {
      if (!order?.team_id) return

      setLoading(true)

      const { response, json } = await fetchOrder(orderId, order.team_id)

      if (response.ok && json.data) {
        const additionalFees = json.data.additional_fees

        additionalFees.forEach((fee) =>
          append({
            id: fee.id,
            name: fee.name,
            fee_type: fee.fee_type,
            value: fee.value,
          })
        )
      }

      setLoading(false)
    }

    if (!fields.length) getFullOrder()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [append, orderId, order?.team_id])

  useEffect(() => {
    const distanceCostSubunit = distance_cost_subunit || 0
    const additionalCostSubunit = order?.additional_cost_subunit || 0

    setTotalPriceSubunit(distanceCostSubunit + additionalCostSubunit)
  }, [distance_cost_subunit, order?.additional_cost_subunit])

  if (!order) return null

  return (
    <>
      <Card.Section>
        <input {...register("distance_cost_subunit", { required: "Podaj kwotę" })} type="hidden" />

        <dl className={cls.costsSummary}>
          <div>
            <dt>Podstawowy koszt dostawy</dt>
            <dd>
              <Field error={errors.distance_cost_subunit?.message}>
                <NumberFormat
                  id="distance_cost"
                  value={formatPrice(distance_cost_subunit, { subunit: true })}
                  onBlur={(e: FormEvent<HTMLInputElement>) => {
                    e.currentTarget.value = formatPrice(e.currentTarget.value)
                  }}
                  onChange={(e: FormEvent<HTMLInputElement>) => {
                    const value = e.currentTarget.value
                    setValue("distance_cost_subunit", value ? Number(value) * 100 : undefined, { shouldValidate: true })
                  }}
                  customInput={Input}
                  decimalScale={2}
                  isNumericString
                  selectOnFocus
                  disabled
                  plain
                />
              </Field>
            </dd>
          </div>
          <div>
            <dt>Koszty dodatkowe</dt>
            <dd>
              {order.additional_cost_subunit !== null
                ? `${formatPrice(order.additional_cost_subunit, { subunit: true })} zł`
                : "-"}
            </dd>
          </div>
          <div>
            <dt>Suma</dt>
            <dd>{formatPrice(totalPriceSubunit, { subunit: true })} zł</dd>
          </div>
        </dl>
      </Card.Section>

      <AdditionalFeesForm append={append} />

      <AdditionalFeesTable additionalFees={additionalFees} updateFee={update} isLoading={isLoading} />
    </>
  )
}

const defaultAdditionalFee = {
  name: "",
  fee_type: ADDITIONAL_FEES_TYPES[0],
  value: "",
}

const AdditionalFeesForm = ({ append }: { append: (value: AdditionalFee) => void }) => {
  const [additionalFee, setAdditionalFee] = useState<AdditionalFee>(defaultAdditionalFee)

  const { t } = useTranslation("orders")

  function setInputValue(e: FormEvent<HTMLInputElement | HTMLSelectElement>) {
    const name = e.currentTarget.name
    const value = e.currentTarget.value

    setAdditionalFee((currentValue) => ({ ...currentValue, [name]: value }))
  }

  function createAdditionalFee() {
    append(additionalFee)
    setAdditionalFee(defaultAdditionalFee)
  }

  return (
    <Card.Section className={cls.additionalFeesForm}>
      <Field
        name="name"
        label="Nazwa"
        inputProps={{
          size: "medium",
          value: additionalFee.name,
          onChange: setInputValue,
        }}
      />

      <Field name="fee_type" label="Rodzaj">
        <Select id="fee_type" name="fee_type" value={additionalFee.fee_type} onChange={setInputValue}>
          {ADDITIONAL_FEES_TYPES.map((feeType) => (
            <option value={feeType} key={feeType}>
              {t(`additional_fee.fee_type.${feeType}`)}
            </option>
          ))}
        </Select>
      </Field>

      <Field
        name="value"
        label="Wartość"
        inputProps={{ size: "medium", type: "number", min: 0, value: additionalFee.value, onChange: setInputValue }}
      />

      <div>
        <Button onClick={createAdditionalFee} color="primary" disabled={!Object.values(additionalFee).every(Boolean)}>
          Dodaj usługę
        </Button>
      </div>
    </Card.Section>
  )
}

type AdditionalFeesTableProps = {
  additionalFees: AdditionalFee[]
  updateFee: UseFieldArrayUpdate<FieldValues, "additional_fees_attributes">
  isLoading: boolean
}

const AdditionalFeesTable = ({ additionalFees, updateFee, isLoading }: AdditionalFeesTableProps) => {
  if (isLoading) {
    return (
      <Card.Section className="text-center">
        <Spinner size="extra-large" />
      </Card.Section>
    )
  }

  if (additionalFees.length === 0) {
    return <Card.Section>Brak kosztów dodatkowych</Card.Section>
  }

  return (
    <Card.Section>
      <table className={cls.costsTable}>
        <thead>
          <tr>
            <th>Nazwa</th>
            <th style={{ width: 170 }}>Typ</th>
            <th style={{ width: 115 }}>Wartość</th>
            <th style={{ width: 115 }} />
          </tr>
        </thead>
        <tbody>
          {additionalFees.map((fee, index) => (
            <AdditionalFeeItem fee={fee} index={index} update={updateFee} key={index} />
          ))}
        </tbody>
      </table>
    </Card.Section>
  )
}
type AdditionalFeeItemProps = {
  fee: AdditionalFee
  index: number
  update: UseFieldArrayUpdate<FieldValues, "additional_fees_attributes">
}

const AdditionalFeeItem = ({ fee, index, update }: AdditionalFeeItemProps) => {
  const [feeToUpdate, setFeeToUpdate] = useState<AdditionalFee | null>(null)

  const { t } = useTranslation("orders")

  function setInputValue(e: FormEvent<HTMLInputElement | HTMLSelectElement>) {
    const name = e.currentTarget.name
    const value = e.currentTarget.value

    setFeeToUpdate((currentValue) => (currentValue ? { ...currentValue, [name]: value } : null))
  }

  if (feeToUpdate) {
    return (
      <tr className={clsx(fee._destroy && cls.removed)} key={index}>
        <td>
          <Input name="name" value={feeToUpdate?.name} size="small" className="p-3" onChange={setInputValue} plain />
        </td>
        <td>
          <Select
            inputSize="small"
            id="fee_type"
            name="fee_type"
            value={feeToUpdate?.fee_type}
            onChange={setInputValue}
            className="pl-2"
          >
            {ADDITIONAL_FEES_TYPES.map((feeType) => (
              <option value={feeType} key={feeType}>
                {t(`additional_fee.fee_type.${feeType}`)}
              </option>
            ))}
          </Select>
        </td>
        <td width={100}>
          <Input name="value" value={feeToUpdate?.value} size="small" className="p-3" onChange={setInputValue} plain />
        </td>

        <td className="text-right">
          <Button
            onClick={() => setFeeToUpdate(null)}
            iconProps={{ name: "close-circle" }}
            variation="contrast"
            color="muted"
            size="small"
            className="mr-2"
            title="Anuluj"
          />

          <Button
            onClick={() => {
              update(index, feeToUpdate)
              setFeeToUpdate(null)
            }}
            iconProps={{ name: "check-circle" }}
            disabled={!feeToUpdate || !feeToUpdate.name || !feeToUpdate.value}
            variation="contrast"
            color="success"
            size="small"
            title="Zapisz"
          />
        </td>
      </tr>
    )
  }

  return (
    <tr className={clsx(fee._destroy && cls.removed)} key={index}>
      <td>{fee.name}</td>
      <td>{t(`additional_fee.fee_type.${fee.fee_type}`)}</td>
      <td>{fee.value}</td>
      <td className="text-right">
        {fee._destroy ? (
          <Button
            onClick={() => update(index, { ...fee, _destroy: false })}
            color="primary"
            variation="contrast"
            size="small"
          >
            Przywróć
          </Button>
        ) : (
          <>
            <Button
              onClick={() => setFeeToUpdate(fee)}
              color="primary"
              variation="contrast"
              size="small"
              iconProps={{ name: "edit" }}
              title="Edytuj"
              className="mr-2"
            />

            <Button
              onClick={() => update(index, { ...fee, _destroy: true })}
              color="alert"
              variation="contrast"
              size="small"
              iconProps={{ name: "bin" }}
              title="Usuń"
            />
          </>
        )}
      </td>
    </tr>
  )
}
